import { Text, View, StyleSheet } from 'react-native';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import Constants from '../../constants';
import WpModal from '../WpModal';
import ModalBody from '../ModalBody';
import ModalFooter from '../ModalFooter';
import { Button, Theme } from '../Button';
import Checkbox from '../Checkbox';
import Input, { Info, Label } from '../Input';
import { CampaignContext, WpCampaignAction, WpFullCampaign } from '../../contexts/campaign';
import { UPDATE_CAMPAIGN } from '../../graphql/campaign';
import {
  DEFAULT_PRO_FOLLOW_UP_INTERVALS,
  ProFollowUpAnchor,
  ProFollowUpInterval,
  PRO_FOLLOW_UP_INTERVALS,
} from '../../utils/proFollowUp';

export default function ProFollowUpSettings({
  isVisible,
  closeModal,
}: {
  isVisible: boolean;
  closeModal: () => void;
}): JSX.Element {
  const { state, dispatch } = React.useContext(CampaignContext);
  const campaign = state.campaign;

  const [enabled, setEnabled] = useState(false);
  const [anchor, setAnchor] = useState<ProFollowUpAnchor>(ProFollowUpAnchor.ENROLLMENT);
  const [intervals, setIntervals] = useState<ProFollowUpInterval[]>([]);
  const [message, setMessage] = useState('');

  const [updateCampaign, updateCampaignData] = useMutation(UPDATE_CAMPAIGN, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!isVisible || !campaign) {
      return;
    }
    setEnabled(!!campaign.proFollowUpEnabled);
    setAnchor(
      campaign.proFollowUpAnchor
      ?? (campaign.hasAppt ? ProFollowUpAnchor.SURGERY : ProFollowUpAnchor.ENROLLMENT),
    );
    setIntervals(campaign.proFollowUpIntervals?.length
      ? [...campaign.proFollowUpIntervals]
      : []);
    setMessage(campaign.proFollowUpMessage ?? '');
  }, [isVisible, campaign?.id]);

  const toggleEnabled = (value: boolean) => {
    setEnabled(value);
    if (value && intervals.length === 0) {
      setIntervals([...DEFAULT_PRO_FOLLOW_UP_INTERVALS]);
    }
  };

  const toggleInterval = (interval: ProFollowUpInterval) => {
    setIntervals((current) => (
      current.includes(interval)
        ? current.filter((item) => item !== interval)
        : [...current, interval]
    ));
  };

  const applyLocalCampaign = (next: Partial<WpFullCampaign>) => {
    if (!campaign) {
      return;
    }
    dispatch({
      type: WpCampaignAction.REFRESH_CAMPAIGN,
      campaign: {
        ...campaign,
        ...next,
      },
    });
  };

  const onSave = () => {
    if (!campaign) {
      return;
    }
    const nextIntervals = enabled ? intervals : [];
    updateCampaign({
      variables: {
        campaignId: campaign.id,
        proFollowUpEnabled: enabled,
        proFollowUpAnchor: anchor,
        proFollowUpIntervals: nextIntervals,
        proFollowUpMessage: message.trim() ? message.trim() : null,
      },
    }).then((result) => {
      const saved = result.data?.updateCampaign;
      if (saved?.success) {
        applyLocalCampaign({
          proFollowUpEnabled: saved.campaign?.proFollowUpEnabled ?? enabled,
          proFollowUpAnchor: saved.campaign?.proFollowUpAnchor ?? anchor,
          proFollowUpIntervals: saved.campaign?.proFollowUpIntervals ?? nextIntervals,
          proFollowUpMessage: saved.campaign?.proFollowUpMessage ?? (message.trim() || null),
        });
        closeModal();
      }
    });
  };

  return (
    <WpModal isVisible={isVisible} closeModal={closeModal}>
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ModalBody title="PRO Follow-ups" closeModal={closeModal} width={520}>
          <Info info="Send the same PRO form again at 1, 3, or 6 months, and at 1 through 10 years. Each reply is a new score and does not overwrite the first one." />

          <View style={styles.row}>
            <Label name="Enable PRO follow-ups" />
            <Checkbox
              selected={enabled}
              onSelect={toggleEnabled}
              style={{ margin: 0 }}
              colorActive={Constants.colors.blue}
            />
          </View>

          {enabled ? (
            <View>
              <Label name="Start the clock from" />
              <View style={styles.choiceRow}>
                <View style={styles.choice}>
                  <Checkbox
                    selected={anchor === ProFollowUpAnchor.SURGERY}
                    onSelect={() => setAnchor(ProFollowUpAnchor.SURGERY)}
                    style={{ marginRight: 8 }}
                    colorActive={Constants.colors.blue}
                  />
                  <Text style={styles.choiceLabel}>Surgery date</Text>
                </View>
                <View style={styles.choice}>
                  <Checkbox
                    selected={anchor === ProFollowUpAnchor.ENROLLMENT}
                    onSelect={() => setAnchor(ProFollowUpAnchor.ENROLLMENT)}
                    style={{ marginRight: 8 }}
                    colorActive={Constants.colors.blue}
                  />
                  <Text style={styles.choiceLabel}>Enrollment date</Text>
                </View>
              </View>
              {anchor === ProFollowUpAnchor.SURGERY ? (
                <Info
                  style={{ marginBottom: 12 }}
                  info="If a patient has no surgery date yet, follow-ups wait until one exists."
                />
              ) : null}

              <Label name="Send again at" />
              <View style={styles.intervalGrid}>
                {PRO_FOLLOW_UP_INTERVALS.map((item) => (
                  <View key={item.value} style={styles.intervalRow}>
                    <Checkbox
                      selected={intervals.includes(item.value)}
                      onSelect={() => toggleInterval(item.value)}
                      style={{ marginRight: 8 }}
                      colorActive={Constants.colors.blue}
                    />
                    <Text style={styles.choiceLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>

              <Input
                style={{ marginTop: 12 }}
                name="Follow-up SMS"
                optional={true}
                multiline={true}
                placeholder="Optional. Leave blank to reuse the campaign's usual message."
                value={message}
                setValue={setMessage}
              />
            </View>
          ) : null}

          <ModalFooter>
            <Button
              key="btn-pro-followup-save"
              text="Save"
              theme={Theme.PRIMARY}
              loading={updateCampaignData.loading}
              disabled={updateCampaignData.loading || !campaign}
              onPress={onSave}
            />
          </ModalFooter>
        </ModalBody>
      </View>
    </WpModal>
  );
}

const styles = StyleSheet.create({
  row: {
    marginVertical: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  choiceRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 8,
    marginTop: 4,
  },
  choice: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  intervalGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  intervalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '50%',
    marginVertical: 4,
  },
  choiceLabel: {
    fontFamily: 'GothamMedium',
    fontSize: 14,
    color: Constants.colors.darkBlue,
  },
});
